import { useToast } from '@chakra-ui/react'
import { createContext, FC, useRef, useState } from 'react'
import { useImmer } from 'use-immer'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  ExternoState,
  IExternoContext,
  IExternoData,
  IExternoItemEdit
} from '@base/@types/providers/FollowUpFornecedor/ExternoProvider'

export const ExternoContext = createContext<IExternoContext>(
  {} as IExternoContext
)

const ExternoProvider: FC = ({ children }) => {
  const [state, setState] = useState<ExternoState>('verificacao')
  const [data, setData] = useState<IExternoData | null>(null)
  const [edits, updateEdits] = useImmer<IExternoItemEdit[]>([])
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [loadingVerificacao, setLoadingVerificacao] = useState(false)
  const [codigoError, setCodigoError] = useState<string | null>(null)
  const tokenRef = useRef<string>('')
  const codigoRef = useRef<string>('')
  const toast = useToast()

  const loadByToken = async (token: string, codigo: string) => {
    tokenRef.current = token
    codigoRef.current = codigo
    setLoadingVerificacao(true)
    setCodigoError(null)
    try {
      const { data: res } = await followUpFornecedorService.getExternoByToken(
        token,
        codigo
      )
      if (!res.success) {
        setCodigoError(res.message ?? 'Código inválido ou expirado.')
        return
      }
      setData(res.data)
      updateEdits(
        res.data.items.map((item: any) => ({
          documentId: item.documentId,
          productId: item.productId,
          novaQuantidade: item.novaQuantidade,
          novaDataEntrega: item.novaDataEntrega,
          observacao: item.observacao
        }))
      )
      setState('editavel')
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404 || status === 410) {
        setState('expirado')
      } else if (!err.response) {
        setCodigoError(
          'Falha de conexão. Verifique sua internet e tente novamente.'
        )
      } else {
        setCodigoError(
          err?.response?.data?.message ?? 'Código inválido. Tente novamente.'
        )
      }
    } finally {
      setLoadingVerificacao(false)
    }
  }

  const handleEditField = (
    documentId: number,
    productId: number,
    field: keyof Pick<
      IExternoItemEdit,
      'novaQuantidade' | 'novaDataEntrega' | 'observacao'
    >,
    value: string | number | null
  ) => {
    updateEdits(draft => {
      const item = draft.find(
        i => i.documentId === documentId && i.productId === productId
      )
      if (item) (item as any)[field] = value === '' ? null : value
    })
  }

  const handleSubmit = async () => {
    if (!data) return
    setLoadingSubmit(true)
    try {
      await followUpFornecedorService.submitExterno(
        tokenRef.current,
        codigoRef.current,
        edits
      )
      setData(prev => (prev ? { ...prev, isSubmitted: true } : prev))
      toast({
        title: 'Respostas enviadas com sucesso!',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top'
      })
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Erro ao enviar respostas'
      toast({ title: msg, status: 'error', duration: 5000, isClosable: true })
    } finally {
      setLoadingSubmit(false)
    }
  }

  return (
    <ExternoContext.Provider
      value={{
        state,
        data,
        edits,
        loadingSubmit,
        loadingVerificacao,
        codigoError,
        loadByToken,
        handleEditField,
        handleSubmit
      }}
    >
      {children}
    </ExternoContext.Provider>
  )
}

export default ExternoProvider
